import { Alert, Form, Spinner } from 'react-bootstrap';

export interface DeclaredValueDraft {
  amount: string;
  reason: string;
}

export const declaredValueChanged = (draft: DeclaredValueDraft, storedAmount: number | null): boolean => {
  const text = draft.amount.trim();
  return text === '' ? storedAmount !== null : Number(text) !== storedAmount;
};

/** This is a whole-line customs amount, never a sale price or a unit price. */
export const declaredValueErrors = (draft: DeclaredValueDraft): { amount?: string; reason?: string } => {
  const errors: { amount?: string; reason?: string } = {};
  const text = draft.amount.trim();
  if (text !== '' && (!/^\d+(\.\d{1,2})?$/.test(text) || !Number.isFinite(Number(text)) || Number(text) <= 0)) {
    errors.amount = 'Enter a line amount above zero with up to two decimal places, or leave empty to clear.';
  }
  if (draft.reason.trim() === '') {
    errors.reason = 'A reason is required to set or clear the declared value.';
  }
  return errors;
};

interface DeclaredValueFieldsProps {
  lineNo: number;
  /** What the line declares while no override is set, shown in the empty box. */
  automaticValue?: string | undefined;
  currency: string;
  draft: DeclaredValueDraft;
  errors: { amount?: string; reason?: string };
  onChange: (draft: DeclaredValueDraft) => void;
  disabled?: boolean;
  previewing?: boolean;
  previewTotal?: string;
  belowAmountPaidWarning?: boolean;
  error?: string;
}

/** Customs is labelled separately: the sale subtotal and total on the line above this editor stay read-only. */
export function DeclaredValueFields({
  lineNo,
  automaticValue,
  currency,
  draft,
  errors,
  onChange,
  disabled = false,
  previewing,
  previewTotal,
  belowAmountPaidWarning,
  error,
}: DeclaredValueFieldsProps) {
  return (
    <div className="declared-value-fields text-start">
      <div>
        <Form.Label className="small mb-1" htmlFor={`declared-value-${lineNo}`}>
          Declared value (customs) · {currency}
        </Form.Label>
        <Form.Control
          id={`declared-value-${lineNo}`}
          size="sm"
          inputMode="decimal"
          placeholder={automaticValue ? `Automatic: ${automaticValue}` : 'Automatic'}
          value={draft.amount}
          disabled={disabled}
          isInvalid={Boolean(errors.amount)}
          onChange={(event) => onChange({ ...draft, amount: event.target.value })}
        />
        {errors.amount && <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>}
        <div className="small text-muted">
          Whole line, not per unit. Empty restores the automatic declaration. Sale amounts do not change.
        </div>
      </div>
      <div>
        <Form.Label className="small mb-1" htmlFor={`declared-reason-${lineNo}`}>
          Reason (set or clear)
        </Form.Label>
        <Form.Control
          id={`declared-reason-${lineNo}`}
          size="sm"
          as="textarea"
          rows={2}
          value={draft.reason}
          disabled={disabled}
          isInvalid={Boolean(errors.reason)}
          onChange={(event) => onChange({ ...draft, reason: event.target.value })}
        />
        {errors.reason && <Form.Control.Feedback type="invalid">{errors.reason}</Form.Control.Feedback>}
      </div>
      <div className="declared-value-status">
        {previewing && (
          <Spinner size="sm" animation="border" role="status" aria-label="Previewing customs declaration" />
        )}
        {previewTotal && <div className="small">Declared total (preview): {previewTotal}</div>}
        {belowAmountPaidWarning && (
          <Alert variant="warning" className="small mt-1 mb-0">
            The declared total is below what the customer paid. You can still save this customs declaration.
          </Alert>
        )}
        {error && (
          <div role="alert" className="text-danger small">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
